import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { CartItem } from '@/hooks/useCart';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

const ITEM_EXPIRATION_TIME = 15 * 60 * 1000; // 15 minutes in ms
const WARNING_TIME = 3 * 60 * 1000; // 3 minutes warning
const INACTIVITY_TIMEOUT = 2 * 60 * 1000; // 2 minutes inactivity

interface ItemExpirationInfo {
  itemId: string;
  addedAt: number;
  expiresAt: number;
  isExpired: boolean;
  isWarning: boolean;
  timeRemaining: number;
}

interface ExpiredItemAction {
  itemId: string;
  productName: string;
  hotelName?: string;
}

export const useCartExpiration = (items: CartItem[]) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const [itemExpirations, setItemExpirations] = useState<Map<string, ItemExpirationInfo>>(() => {
    try {
      const savedExpirations = localStorage.getItem('cart-item-expirations');
      if (!savedExpirations) return new Map();
      const parsed = JSON.parse(savedExpirations) as Record<string, ItemExpirationInfo>;
      return new Map(Object.entries(parsed));
    } catch {
      return new Map();
    }
  });
  const [expiredItems, setExpiredItems] = useState<ExpiredItemAction[]>([]);
  const [showExpirationDialog, setShowExpirationDialog] = useState(false);
  const [isAbandonedSession, setIsAbandonedSession] = useState(false);
  const [showWarningToast, setShowWarningToast] = useState(false);

  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<Set<string>>(new Set());
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stabilize items reference (React Query may give a new array identity on re-render)
  const itemsRef = useRef<CartItem[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const itemsKey = useMemo(() => items.map(i => i.id).join('|'), [items]);

  // Track user activity (stable callback)
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsAbandonedSession(false);

    // Reset inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (itemsRef.current.length > 0) {
      inactivityTimerRef.current = setTimeout(() => {
        setIsAbandonedSession(true);
      }, INACTIVITY_TIMEOUT);
    }
  }, []);

  // Initialize activity listeners
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    // Initial activity update
    updateActivity();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [updateActivity]);

  // (Removed) localStorage bootstrap effect: itemExpirations is initialized from localStorage in useState initializer

  // Update expiration info for items
  useEffect(() => {
    const currentItems = itemsRef.current;

    if (!currentItems.length) {
      setItemExpirations(new Map());
      localStorage.removeItem('cart-item-expirations');
      warningShownRef.current.clear();
      return;
    }

    setItemExpirations(prev => {
      const newMap = new Map(prev);
      const now = Date.now();

      // Add new items
      currentItems.forEach(item => {
        if (!newMap.has(item.id)) {
          const addedAt = now;
          const expiresAt = addedAt + ITEM_EXPIRATION_TIME;
          newMap.set(item.id, {
            itemId: item.id,
            addedAt,
            expiresAt,
            isExpired: false,
            isWarning: false,
            timeRemaining: ITEM_EXPIRATION_TIME,
          });
        }
      });

      // Remove items that are no longer in cart
      const itemIds = new Set(currentItems.map(i => i.id));
      newMap.forEach((_, key) => {
        if (!itemIds.has(key)) {
          newMap.delete(key);
          warningShownRef.current.delete(key);
        }
      });

      // Save to localStorage
      const obj: Record<string, ItemExpirationInfo> = {};
      newMap.forEach((value, key) => {
        obj[key] = value;
      });
      localStorage.setItem('cart-item-expirations', JSON.stringify(obj));

      return newMap;
    });
  }, [itemsKey]);

  // Timer to update expiration states
  useEffect(() => {
    if (!itemsRef.current.length) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const newExpiredItems: ExpiredItemAction[] = [];
      let hasWarningItems = false;

      setItemExpirations(prev => {
        const newMap = new Map(prev);

        newMap.forEach((info, itemId) => {
          const timeRemaining = Math.max(0, info.expiresAt - now);
          const isExpired = timeRemaining <= 0;
          const isWarning = !isExpired && timeRemaining <= WARNING_TIME;

          newMap.set(itemId, {
            ...info,
            timeRemaining,
            isExpired,
            isWarning,
          });

          // Track expired items
          if (isExpired) {
            const item = itemsRef.current.find(i => i.id === itemId);
            if (item) {
              const details = item.booking_details as any;
              newExpiredItems.push({
                itemId,
                productName: item.product?.name_es || item.product?.name || 'Item',
                hotelName: details?.hotelName,
              });
            }
          }

          // Show warning toast once per item
          if (isWarning && !warningShownRef.current.has(itemId)) {
            warningShownRef.current.add(itemId);
            hasWarningItems = true;
          }
        });

        return newMap;
      });

      // Show warning toast
      if (hasWarningItems && !showWarningToast) {
        setShowWarningToast(true);
        toast({
          title: t('cart.expirationWarning'),
          description: t('cart.expirationWarningDescription'),
          variant: 'destructive',
          duration: 10000,
        });
      }

      // Show expiration dialog for expired items
      if (newExpiredItems.length > 0) {
        setExpiredItems(current => {
          // Only add items that aren't already in the list
          const existingIds = new Set(current.map(i => i.itemId));
          const newItems = newExpiredItems.filter(i => !existingIds.has(i.itemId));
          if (newItems.length > 0) {
            const updated = [...current, ...newItems];
            setShowExpirationDialog(true);
            return updated;
          }
          return current;
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [itemsKey, t, toast, showWarningToast]);

  // Check for returning user with stale items (run once per mount, after we have items)
  const staleCheckRef = useRef(false);
  useEffect(() => {
    if (!itemsRef.current.length) return;
    if (staleCheckRef.current) return;
    staleCheckRef.current = true;

    const now = Date.now();
    const staleItems: ExpiredItemAction[] = [];

    itemsRef.current.forEach(item => {
      const info = itemExpirations.get(item.id);
      if (info && info.expiresAt < now) {
        const details = item.booking_details as any;
        staleItems.push({
          itemId: item.id,
          productName: item.product?.name_es || item.product?.name || 'Item',
          hotelName: details?.hotelName,
        });
      }
    });

    if (staleItems.length > 0) {
      setExpiredItems(staleItems);
      setShowExpirationDialog(true);
    }
  }, [itemsKey, itemExpirations]);

  // Get the minimum time remaining across all items
  const minTimeRemaining = (() => {
    let min = Infinity;
    itemExpirations.forEach(info => {
      if (info.timeRemaining < min) {
        min = info.timeRemaining;
      }
    });
    return min === Infinity ? 0 : min;
  })();

  // Reset expiration for an item (after revalidation)
  const resetItemExpiration = useCallback((itemId: string) => {
    const now = Date.now();
    setItemExpirations(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(itemId);
      if (existing) {
        newMap.set(itemId, {
          ...existing,
          addedAt: now,
          expiresAt: now + ITEM_EXPIRATION_TIME,
          isExpired: false,
          isWarning: false,
          timeRemaining: ITEM_EXPIRATION_TIME,
        });
      }
      warningShownRef.current.delete(itemId);
      return newMap;
    });

    // Remove from expired items
    setExpiredItems(prev => prev.filter(i => i.itemId !== itemId));
    
    if (expiredItems.length <= 1) {
      setShowExpirationDialog(false);
    }
  }, [expiredItems.length]);

  // Mark items as handled (removed from dialog)
  const dismissExpiredItems = useCallback((itemIds: string[]) => {
    setExpiredItems(prev => prev.filter(i => !itemIds.includes(i.itemId)));
    if (expiredItems.length - itemIds.length <= 0) {
      setShowExpirationDialog(false);
    }
  }, [expiredItems.length]);

  // Reset session after abandonment detection
  const resetSession = useCallback(() => {
    setIsAbandonedSession(false);
    updateActivity();
  }, [updateActivity]);

  return {
    itemExpirations,
    expiredItems,
    showExpirationDialog,
    setShowExpirationDialog,
    isAbandonedSession,
    minTimeRemaining,
    resetItemExpiration,
    dismissExpiredItems,
    resetSession,
  };
};
